import { t, type I18nLocale } from '@ardoq/i18n';
import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';
import type { TreemapSizing } from '../viewModel/toSizing';
import { TILING_METHODS } from '../view/standard/consts';
import type { TreemapTilingMethod } from '../types';

/**
 * What the settings panel shows beside "Size by field", derived from the sizing rather than from
 * the setting.
 *
 * The workspace-mode version asks whether `sizeByFieldName` is non-empty, which is not the
 * question `toSizing` answers: a name also has to be among the fields the view offers and not
 * confidential. Reading the setting a second time made the panel and the picture disagree on both
 * of the routes those rules exist for — a persisted name the view no longer offers printed the raw
 * field name, because the `Component count` branch is guarded by emptiness alone and a `find` miss
 * falls to the `?? sizeByFieldName` tail; a persisted confidential field printed its own label,
 * because it is in the offered list rather than absent from it.
 *
 * Taking the `TreemapSizing` the hierarchy was summed with removes the second copy of the rule
 * rather than duplicating the fix into it.
 */
export const sizeByFieldDisplayValue = (
  sizing: TreemapSizing,
  numericFields: FieldWithPermissions[],
  i18nLocale: I18nLocale
): string => {
  if (sizing.kind === 'count') {
    return t(
      i18nLocale,
      'tabview:hierarchicalTreemap.componentCount',
      'Component count'
    );
  }
  return (
    numericFields.find(({ name }) => name === sizing.fieldName)?.label ??
    sizing.fieldName
  );
};

export const tilingMethodDisplayValue = (
  tilingMethod: TreemapTilingMethod,
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    `tabview:hierarchicalTreemap.tiling.${tilingMethod}` as Parameters<
      typeof t
    >[1],
    TILING_METHODS[tilingMethod].label
  );

export const maxDepthDisplayValue = (
  maxDepth: number,
  i18nLocale: I18nLocale
): string =>
  maxDepth <= 0
    ? t(i18nLocale, 'tabview:hierarchicalTreemap.allLevels', 'All levels')
    : String(maxDepth);
